import { z } from 'zod';

/**
 * Defaults públicos seguros para permitir o boot do app em ambientes de
 * preview onde nem todas as variáveis estão configuradas (ex.: deploy de
 * preview do Vercel/Cloudflare Pages que só define VITE_API_URL).
 *
 * - URLs de websocket e de entrega de imagens são endpoints públicos, então
 *   caem para os valores de produção sem risco de vazar segredos.
 * - VITE_GOOGLE_API_KEY NÃO tem default: é uma credencial e não deve ser
 *   embutida no código. Sem ela, os recursos de mapa são desabilitados, mas
 *   o app continua inicializando normalmente.
 */
const DEFAULT_WEBSOCKET_URL = 'wss://socket.pratigo.com.br';
const DEFAULT_IMAGE_URL = 'https://imagedelivery.net/Yl80pGeQrAUuLCQPer_Y-w';

// Trata strings vazias/em branco como ausentes para que o `.default()`/opcional
// seja aplicado (o Vite injeta variáveis não definidas como `undefined`, mas um
// valor vazio explícito não dispararia o default).
const emptyToUndefined = (val: unknown) =>
  typeof val === 'string' && val.trim() === '' ? undefined : val;

const envSchema = z.object({
  VITE_API_URL: z.string().url('VITE_API_URL deve ser uma URL válida'),
  VITE_WEBSOCKET_URL: z.preprocess(
    emptyToUndefined,
    z.string().url('VITE_WEBSOCKET_URL deve ser uma URL válida').default(DEFAULT_WEBSOCKET_URL)
  ),
  VITE_IMAGE_URL: z.preprocess(
    emptyToUndefined,
    z.string().url('VITE_IMAGE_URL deve ser uma URL válida').default(DEFAULT_IMAGE_URL)
  ),
  VITE_CLARITY_PROJECT_ID: z.preprocess(emptyToUndefined, z.string().optional()),
  VITE_GOOGLE_API_KEY: z.preprocess(emptyToUndefined, z.string().optional()),
  // PRAT-165: app do OneSignal (notificações push). Sem ele o app não oferece push.
  VITE_ONESIGNAL_APP_ID: z.preprocess(emptyToUndefined, z.string().optional()),
  // PRAT-43: endereço(s) cadastrado(s) no app do OneSignal (Site URL), separados por vírgula.
  // Web push vale para um endereço só: fora dele (ex.: domínio exclusivo da loja) não oferece.
  VITE_ONESIGNAL_HOSTS: z.preprocess(emptyToUndefined, z.string().optional()),
  // PRAT-165: só no ambiente local — inscrição simulada, sem OneSignal.
  VITE_PUSH_SIMULATOR: z.preprocess(emptyToUndefined, z.string().optional()),
});

const envParse = envSchema.safeParse(import.meta.env);

if (!envParse.success) {
  console.error('❌ Variáveis de ambiente inválidas:', envParse.error.format());
  throw new Error('Variáveis de ambiente inválidas');
}

export const env = envParse.data;

// Avisa quando variáveis não-sensíveis caíram para o default (útil em preview),
// sem impedir o boot do app.
if (env.VITE_WEBSOCKET_URL === DEFAULT_WEBSOCKET_URL && !import.meta.env.VITE_WEBSOCKET_URL) {
  console.warn('⚠️ VITE_WEBSOCKET_URL não definida — usando o endpoint padrão de produção.');
}
if (env.VITE_IMAGE_URL === DEFAULT_IMAGE_URL && !import.meta.env.VITE_IMAGE_URL) {
  console.warn('⚠️ VITE_IMAGE_URL não definida — usando o endpoint padrão de produção.');
}
if (!env.VITE_GOOGLE_API_KEY) {
  console.warn('⚠️ VITE_GOOGLE_API_KEY não definida — recursos de mapa ficarão indisponíveis.');
}

// Log apenas em desenvolvimento
if (import.meta.env.MODE === 'development') {
  console.log('✅ Variáveis de ambiente carregadas:', {
    API_URL: env.VITE_API_URL,
    WEBSOCKET_URL: env.VITE_WEBSOCKET_URL,
    IMAGE_URL: env.VITE_IMAGE_URL,
    CLARITY_PROJECT_ID: env.VITE_CLARITY_PROJECT_ID,
    GOOGLE_API_KEY: env.VITE_GOOGLE_API_KEY ? '***' : undefined,
    MODE: import.meta.env.MODE,
  });
}
